import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { IAdaptMenuItem } from "@common/ux/menu/menu.component";
import isEqual from "lodash.isequal";
import { EMPTY, merge, timer } from "rxjs";
import { catchError, switchMap, take } from "rxjs/operators";
import { ColumnSizePresetGroups, ILayoutBase, ILayoutComponent } from "../layout.interface";
import { LayoutService } from "../layout.service";
import { LayoutManagerComponent } from "../layout-manager/layout-manager.component";

@Component({
    selector: "adapt-layout-manager-controls",
    templateUrl: "./layout-manager-controls.component.html",
    styleUrls: ["./layout-manager-controls.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class LayoutManagerControlsComponent extends BaseComponent implements OnInit {
    @Input({ required: true }) public layoutManager!: LayoutManagerComponent;

    public saveError?: string;
    public componentMenu!: IAdaptMenuItem[];
    public pageColumnMenu!: IAdaptMenuItem[];

    public ngOnInit() {
        merge(
            this.layoutManager.initialised,
            this.layoutManager.layoutUpdated$,
            this.layoutManager.isEditing$,
        ).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateMenus());

        // clear error when entering/exiting edit mode
        this.layoutManager.isEditing$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.saveError = undefined);
    }

    @Autobind
    public saveLayout() {
        this.saveError = undefined;
        return this.layoutManager.saveLayout().pipe(
            catchError((e) => {
                this.saveError = ErrorHandlingUtilities.getHttpResponseMessage(e);
                return EMPTY;
            }),
            switchMap(() => this.layoutManager.toggleEditing(false)),
        );
    }

    private getColumnSizes(layout?: ILayoutBase) {
        if (!layout) {
            return [];
        }
        return layout.columns.filter((col) => col.options.enabled)
            .map((col) => col.size ?? "");
    }

    private updateMenus() {
        this.updateComponentMenu();
        this.updatePageColumnMenu();
    }

    private updateComponentMenu() {
        const items: IAdaptMenuItem[] = [];

        if (this.layoutManager.useHiddenComponentMenu) {
            const hiddenComponents: ILayoutComponent[] = [];

            for (const column of this.layoutManager.layout.columns) {
                for (const component of column.options.components) {
                    if (component.options?.userHidden) {
                        hiddenComponents.push(component);
                    }
                }
            }

            for (const component of hiddenComponents.sort(SortUtilities.getSortByFieldFunction("options.displayName"))) {
                const displayName = component.options?.displayName;
                if (!displayName) {
                    this.log.warn(`Component ${component.id} has no displayName set for useHiddenComponentMenu`);
                    continue;
                }
                items.push({
                    text: displayName ?? component.id,
                    icon: "fal fa-plus",
                    onClick: () => {
                        LayoutService.setOptions(component, { userHidden: false });

                        // move to component once the layout has updated
                        this.layoutManager.layoutUpdated$.pipe(
                            take(1),
                            switchMap(() => timer(100)),
                        ).subscribe(() => {
                            component.element?.scrollIntoView({ behavior: "smooth" });
                            component.element?.classList.add("highlight");
                            setTimeout(() => component.element?.classList.remove("highlight"), 2500);
                        });

                        this.layoutManager.updateLayout();
                    },
                });
            }

            // add a placeholder when nothing to add
            if (items.length === 0) {
                items.push({
                    text: "All components added!",
                    disabled: true,
                });
            }
        }

        this.componentMenu = [{
            text: "Add components",
            icon: "fal fa-plus-square",
            items,
        }];
    }

    private updatePageColumnMenu() {
        const enabledColumnSizes = this.getColumnSizes(this.layoutManager.currentLayout);

        const generateMenuItem = (text: string, sizes: string[]) => {
            return {
                text,
                icon: "",
                onClick: () => this.layoutManager.resizeLayout(sizes).subscribe(),
                isChecked: isEqual(enabledColumnSizes, sizes),
            } as IAdaptMenuItem;
        };

        const items: IAdaptMenuItem[] = [];

        for (const [text, columnSizes] of Object.entries(ColumnSizePresetGroups)) {
            // ungrouped, no children
            if (text === "") {
                for (const columnSize of columnSizes) {
                    const preset = this.layoutManager.columnSizePresets[columnSize];
                    if (preset) {
                        items.push(generateMenuItem(columnSize, preset));
                    }
                }
            } else {
                if (columnSizes.length > 0) {
                    items.push({
                        text,
                        icon: "",
                        disabled: true,
                        separatorTop: true,
                    });

                    for (const columnSize of columnSizes) {
                        const preset = this.layoutManager.columnSizePresets[columnSize];
                        if (preset) {
                            items.push(generateMenuItem(columnSize, preset));
                        }
                    }
                }
            }
        }

        this.pageColumnMenu = [{
            text: "Page columns",
            icon: "fal fa-columns",
            items,
        }];
    }
}
